import React from 'react';
import { motion } from 'framer-motion';
import { Github } from 'lucide-react';

const Projects: React.FC = () => {
  const projects = [
    {
      title: 'AI Mock Interview Agent',
      description: 'Built a 5-node LangGraph multi-agent pipeline with FastAPI and React to generate interview questions, score responses (STAR, clarity, technical depth), and deliver structured feedback with sub-5s latency.',
      image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2960&auto=format&fit=crop',
      technologies: ['React', 'FastAPI', 'LangGraph', 'Claude', 'SQLite'],
      githubUrl: 'https://github.com/SamirTalkal',
      featured: true
    },
    {
      title: 'RAG Document Search System',
      description: 'Developed an end-to-end RAG document search system using FastAPI and LangGraph, supporting document ingestion, processing, and low-latency (<4s) retrieval from diverse data sources.',
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=2960&auto=format&fit=crop',
      technologies: ['Python', 'FastAPI', 'LangGraph', 'Vector Stores'],
      githubUrl: 'https://github.com/SamirTalkal',
      featured: true
    }
  ];

  return (
    <section id="projects" className="section-padding">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Featured Projects
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            A showcase of my recent work in AI agents, LLM-powered systems, and RAG pipelines
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-dark-700 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600 overflow-hidden card-hover"
            >
              {/* Project Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                />
                {project.featured && (
                  <div className="absolute top-4 left-4 bg-black/70 dark:bg-white/70 backdrop-blur-sm text-white dark:text-gray-900 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                    Featured
                  </div>
                )}
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {project.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-2 py-1 bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-200 text-xs rounded"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Project Links */}
                <div className="flex space-x-3">
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm font-medium transition-colors"
                  >
                    <Github size={16} className="mr-1" />
                    Code
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View More Projects Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="https://github.com/SamirTalkal"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center"
          >
            <Github size={18} className="mr-2" />
            View More Projects
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 